import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from "@mui/material"
import { translate } from "app/language/service"
import { ISortOrder } from "app/model"
import { IForm, IFormOperatorRow } from "form/model"
import { synchronizeNewOperatorSelection } from "form/tools/operator"
import { getComparator } from "form/tools/ssp"
import { getUmbracityStringId } from "form/tools/ssp-operator-umbracity"
import { IOperator } from "operator/model"
import { getStringOperatorId } from "operator/tools/common"
import React from "react"
import { OrderByArrow } from "ui/components/OrderByArrow"
import { stableSort } from "ui/tools/list"
import { ColumnHeader } from "../../../../ui/components/ColumnHeader"
import { OperatorRow } from "./OperatorRow"

interface IOperatorListProps {
  form: IForm
  sspIndex: number
  rowRenderIndex: number
  isSspSelected: boolean
  allOperators: IOperator[]
  selectedOperators: IOperator[]
  filteredOperatorRows: IFormOperatorRow[]
  isUmbracity: boolean
}

type IRowKey = keyof IFormOperatorRow

export const OperatorList = React.memo(
  ({
    form,
    sspIndex,
    rowRenderIndex,
    allOperators,
    selectedOperators,
    isSspSelected,
    filteredOperatorRows,
    isUmbracity
  }: IOperatorListProps): JSX.Element => {
    const [orderBy, setOrderBy] = React.useState<IRowKey>("publisherName")
    const [order, setOrder] = React.useState<ISortOrder>("asc")
    const [deselectAllIsActif, setDeselectAllIsActif] = React.useState(false)
    const [selectedOperatorIds, setSelectedOperatorIds] = React.useState<string[]>(
      isUmbracity ? [getUmbracityStringId()] : selectedOperators.map(o => getStringOperatorId(o))
    )
    const { isLoadingAllOperators, isSynchronizingSsp, hasLoadingAllOperatorsError } = form.screens
    const totalOperators = allOperators.length
    const emptyMessage =
      totalOperators === 0 && filteredOperatorRows.length === 0
        ? translate("campaign.operator.not.available")
        : filteredOperatorRows.length === 0
          ? translate("campaign.operator.filter.empty")
          : ""
    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: IRowKey) => {
      const isAsc = orderBy === property && order === "asc"
      setOrder(isAsc ? "desc" : "asc")
      setOrderBy(property)
    }
    React.useEffect(() => {
      if (isUmbracity) {
        return
      }
      setSelectedOperatorIds(selectedOperators.map(o => getStringOperatorId(o)))
    }, [rowRenderIndex])
    const areCheckboxesDisabled = !isSspSelected || isUmbracity || isSynchronizingSsp
    const simulateAllAsSelected = selectedOperatorIds.length === 0 && !deselectAllIsActif
    return hasLoadingAllOperatorsError ? (
      <Typography
        id={`operator-list-${sspIndex}-error`}
        color="error"
        fontSize=".85rem"
        sx={{ p: "1rem", pl: "5.5rem", borderBottom: theme => theme.palette.border }}>
        {translate("operator.loading.all.error")}
      </Typography>
    ) : isLoadingAllOperators ? (
      <Typography
        id={`operator-list-${sspIndex}-loading`}
        color="textSecondary"
        fontSize=".85rem"
        sx={{ p: "1rem", pl: "5.5rem", borderBottom: theme => theme.palette.border }}>
        {translate("operator.loading.all")}
      </Typography>
    ) : emptyMessage ? (
      <Typography
        id={`operator-list-${sspIndex}-empty`}
        color="textSecondary"
        fontSize=".85rem"
        sx={{ p: "1rem", pl: "5.5rem", borderBottom: theme => theme.palette.border }}>
        {emptyMessage}
      </Typography>
    ) : (
      <TableContainer
        sx={{
          "& .MuiTableCell-root": { pt: 0, pb: 0, fontSize: ".8rem", fontWeight: 300 },
          "& .MuiTableCell-root.MuiTableCell-head": {
            fontSize: ".85rem",
            fontWeight: 500
          }
        }}>
        <Table aria-label="collapsible table">
          <TableHead sx={{ backgroundColor: theme => theme.palette.background.paper }}>
            <TableRow
              sx={{
                "height": "3rem",
                "& .MuiTableSortLabel-icon": {
                  ml: "1rem",
                  mt: "-1rem"
                }
              }}>
              <TableCell width="1rem">{/* padding left */}</TableCell>
              <TableCell colSpan={2} sx={{ position: "relative" }}>
                <TableSortLabel
                  active={orderBy === "publisherName"}
                  direction={orderBy === "publisherName" ? order : "asc"}
                  onClick={(event: React.MouseEvent<unknown>) => handleRequestSort(event, "publisherName")}>
                  <ColumnHeader id={`operator-list-${sspIndex}-title`} primaryText={translate("operator.name")} />
                  <OrderByArrow order={order} currentOrderBy={orderBy} columnOrderBy="publisherName" />
                </TableSortLabel>
                {!isUmbracity && allOperators.length > 1 && (
                  <Typography
                    id={`operator-list-${sspIndex}-select-all-button`}
                    color={isSspSelected ? "textPrimary" : "textSecondary"}
                    onClick={() => {
                      if (!isSspSelected) {
                        return
                      }
                      if (selectedOperatorIds.length === 0) {
                        setDeselectAllIsActif(!deselectAllIsActif)
                        return
                      }
                      setDeselectAllIsActif(false)
                      synchronizeNewOperatorSelection([])
                      setSelectedOperatorIds([])
                    }}
                    fontSize=".8rem"
                    sx={{
                      "position": "absolute",
                      "bottom": 0,
                      "cursor": isSspSelected ? "pointer" : "default",
                      "&:hover": {
                        color: isSspSelected ? theme => theme.palette.action.active : undefined
                      }
                    }}>
                    {translate(simulateAllAsSelected ? "label.deselect.all" : "label.select.all")}
                  </Typography>
                )}
              </TableCell>
              {/*
            <TableCell>
              <TableSortLabel
                active={orderBy === "cpm"}
                direction={orderBy === "cpm" ? order : "asc"}
                onClick={(event: React.MouseEvent<unknown>) => handleRequestSort(event, "cpm")}>
                <ColumnHeader primaryText={`${translate("label.cpm")} (${getCurrencySymbol()})`} />
                <OrderByArrow order={order} currentOrderBy={orderBy} columnOrderBy="cpm" />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <ColumnHeader
                primaryText={translate("label.impression.ratio.primary")}
                secondaryText={translate("label.impression.ratio.secondary")}
              />
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "impressionPercent"}
                direction={orderBy === "impressionPercent" ? order : "asc"}
                onClick={(event: React.MouseEvent<unknown>) => handleRequestSort(event, "impressionPercent")}>
                <ColumnHeader primaryText={translate("label.impression.percent")} />
                <OrderByArrow order={order} currentOrderBy={orderBy} columnOrderBy="impressionPercent" />
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <ColumnHeader
                primaryText={translate("label.screen.ratio.primary")}
                secondaryText={translate("label.screen.ratio.secondary")}
              />
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === "screensPercent"}
                direction={orderBy === "screensPercent" ? order : "asc"}
                onClick={(event: React.MouseEvent<unknown>) => handleRequestSort(event, "screensPercent")}>
                <ColumnHeader primaryText={translate("label.screen.percent")} />
                <OrderByArrow order={order} currentOrderBy={orderBy} columnOrderBy="screensPercent" />
              </TableSortLabel>
            </TableCell>
            */}
              <TableCell width="1rem">{/* padding right */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(filteredOperatorRows, getComparator(order, orderBy)).map((row, rowIndex) => (
              <OperatorRow
                key={row.id}
                rowIndex={rowIndex}
                sspIndex={sspIndex}
                row={row}
                isSelected={
                  isUmbracity || totalOperators === 1
                    ? isSspSelected
                    : simulateAllAsSelected
                      ? true
                      : selectedOperatorIds.includes(row.id)
                }
                disabled={areCheckboxesDisabled || totalOperators === 1 || isUmbracity}
                onSelectionChange={(selectedRow, checked) => {
                  let newSelectedIds: string[]
                  if (simulateAllAsSelected) {
                    // means no operator is selected but UI display them as selected,
                    // here user unselect one, so we have to select all but this one
                    newSelectedIds = allOperators.map(o => getStringOperatorId(o)).filter(id => id !== selectedRow.id)
                  } else {
                    newSelectedIds = checked
                      ? [...selectedOperatorIds, selectedRow.id]
                      : selectedOperatorIds.filter(id => id !== selectedRow.id)
                    if (newSelectedIds.length === allOperators.length) {
                      newSelectedIds = []
                    }
                  }
                  synchronizeNewOperatorSelection(newSelectedIds)
                  setSelectedOperatorIds(newSelectedIds)
                  if (newSelectedIds.length === 0 && deselectAllIsActif) {
                    setDeselectAllIsActif(false)
                  }
                }}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }
)
